import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TreeSelect } from 'antd';
import { DefaultOptionType, TreeSelectProps } from 'rc-tree-select/lib/TreeSelect';
import { Spinner } from 'components';

import { type ViewClaimFilters } from 'all-in-one-shared-api';
import { CLAIM_TYPE_ALL_OP, extractClaimTypesAndBenefits, getTreeOptions } from '../../utils';
import { useGetClaimTypesByMemberTypes } from 'hooks/useGetClaimTypesByMemberTypes';

interface ClaimTypeDropdownProps {
  selectedPatientMemberTypes: string[];
  onChange: React.Dispatch<React.SetStateAction<ViewClaimFilters>>;
}

const ClaimTypeDropdown = ({ selectedPatientMemberTypes, onChange }: ClaimTypeDropdownProps) => {
  const { t, i18n } = useTranslation();
  const [treeData, setTreeData] = useState<DefaultOptionType[]>([]);
  const [claimDropdownValues, setClaimDropdownValues] = useState<string[]>([]);

  const { claimTypes: options } = useGetClaimTypesByMemberTypes(selectedPatientMemberTypes);

  useEffect(() => {
    if (!options.length) return;
    const treeOptions = getTreeOptions(options, i18n.language);
    setTreeData(treeOptions);
  }, [options, i18n.language]);

  const getAllClaimTypeOptionKeys = (allOptionType = CLAIM_TYPE_ALL_OP) => {
    const optionKeys: string[] = [allOptionType];
    options.forEach((option) => {
      if (!option.code || option.code !== getCodeFromOptionAll(allOptionType)) {
        return;
      }

      if (option?.benefits?.length) {
        optionKeys.push(...option.benefits.map((childOption) => `${option.code}__${childOption.code}`));
      } else {
        optionKeys.push(option.code);
      }
    });
    return optionKeys;
  };
  const hasOptionAll = (values: string[], code = CLAIM_TYPE_ALL_OP) => !!values.find((value) => value.startsWith(code));

  const getCodeFromOptionAll = (optionAll: string) => {
    const splitData = optionAll.split('__');
    return splitData.length > 1 ? splitData[1] : splitData[0];
  };

  const handleLogicOfOptionAllOP = (newChanges: string[]) => {
    let values = newChanges;

    const isNotSelectedAllOptions = (oldValues: string[], newValues: string[]) => newValues.length < oldValues.length;

    const code = getCodeFromOptionAll(CLAIM_TYPE_ALL_OP);
    const oldValues = claimDropdownValues.filter((value) => value.startsWith(code) || value === CLAIM_TYPE_ALL_OP);
    const newValues = newChanges.filter((value) => value.startsWith(code) || value === CLAIM_TYPE_ALL_OP);
    if (hasOptionAll(newValues, CLAIM_TYPE_ALL_OP)) {
      if (isNotSelectedAllOptions(oldValues, newValues)) {
        values = newChanges.filter((value) => value !== CLAIM_TYPE_ALL_OP);
      } else {
        values = Array.from(new Set<string>([...values, ...getAllClaimTypeOptionKeys(CLAIM_TYPE_ALL_OP)]));
      }
    } else if (hasOptionAll(oldValues, CLAIM_TYPE_ALL_OP)) {
      values = values.filter((value) => !value.startsWith(code));
    } else if (getAllClaimTypeOptionKeys(CLAIM_TYPE_ALL_OP).length - 1 === newValues.length) {
      values = [CLAIM_TYPE_ALL_OP, ...newChanges];
    }
    return values;
  };

  const filterChanged = (changedValues: string[]) => {
    const values = handleLogicOfOptionAllOP(changedValues);
    setClaimDropdownValues(values);
    const { claimTypes, benefitCodes } = extractClaimTypesAndBenefits(values);
    onChange((state) => {
      return {
        ...state,
        claimTypes: [...claimTypes],
        ...(benefitCodes && benefitCodes.length ? { benefitCodes: [...benefitCodes] } : { benefitCodes: undefined }),
      };
    });
  };

  const treeProps: TreeSelectProps = {
    treeCheckable: true,
    value: claimDropdownValues,
    multiple: true,
    allowClear: true,
    showSearch: false,
    treeData: treeData,
    showCheckedStrategy: TreeSelect.SHOW_PARENT,
    maxTagCount: 1,
    style: {
      minWidth: 250,
    },
    dropdownStyle: {
      minWidth: 400,
    },
    placeholder: t('claims.allClaimTypes'),
    onChange: filterChanged,
    dropdownRender: (originNode: React.ReactElement) => {
      return options.length ? originNode : <Spinner />;
    },
    treeIcon: true,
  };

  return <TreeSelect data-testid="mock-TreeSelect" {...treeProps} />;
};

export default ClaimTypeDropdown;
